import { SOUND_OPTIONS } from '../common/options.js';
import { DATA_MANAGER_STORE_KEYS, dataManager } from './data-manager.js';

/**
 * Whether the master sound switch is ON (the global mute master).
 * @returns {boolean}
 */
function masterSoundOn() {
  return dataManager.store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_SOUND) === SOUND_OPTIONS.ON;
}

/**
 * Whether background music is enabled (master ON + music channel not OFF). The music channel
 * defaults to ON when the value has never been set (back-compat with older saves).
 * @returns {boolean}
 */
function musicOn() {
  if (!masterSoundOn()) {
    return false;
  }
  return dataManager.store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_MUSIC) !== SOUND_OPTIONS.OFF;
}

/**
 * Whether sound effects are enabled (master ON + sfx channel not OFF). Defaults ON.
 * @returns {boolean}
 */
function sfxOn() {
  if (!masterSoundOn()) {
    return false;
  }
  return dataManager.store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_SFX) !== SOUND_OPTIONS.OFF;
}

/**
 * @param {Phaser.Scene} scene The Phaser 3 scene to play audio in
 * @param {string} audioKey The key of the audio asset that should be played
 * @returns {void}
 */
export function playBackgroundMusic(scene, audioKey) {
  if (!musicOn()) {
    return;
  }

  // get all of the audio objects that are currently playing so we can check if the sound we
  // want to play is already playing, and to stop all other sounds
  const existingSounds = scene.sound.getAllPlaying();
  let musicAlreadyPlaying = false;

  existingSounds.forEach((sound) => {
    if (sound.key === audioKey) {
      musicAlreadyPlaying = true;
      return;
    }
    sound.stop();
  });

  if (!musicAlreadyPlaying) {
    scene.sound.play(audioKey, {
      loop: true,
    });
  }
}

/**
 * @param {Phaser.Scene} scene The Phaser 3 scene to play audio in
 * @param {string} audioKey The key of the audio asset that should be played
 * @returns {void}
 */
export function playSoundFx(scene, audioKey, gain = 1) {
  if (!sfxOn()) {
    return;
  }

  const baseVolume = dataManager.store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_VOLUME) * 0.25;

  // `gain` (default 1) lets a caller soften a specific cue — the coin pickup + grass rustle were
  // piercingly loud relative to everything else.
  scene.sound.play(audioKey, {
    volume: 20 * baseVolume * gain,
  });
}

/**
 * Play a walking footstep — deliberately QUIET (low volume) so it adds ambience without being
 * annoying. Respects the SFX channel + global mute like every other effect.
 * @param {Phaser.Scene} scene The Phaser 3 scene to play audio in
 * @param {string} audioKey The key of the footstep audio asset
 * @returns {void}
 */
export function playFootstepFx(scene, audioKey) {
  if (!sfxOn()) {
    return;
  }
  // Footsteps are pure ambience — keep them very soft so they never nag (esp. the grass loop).
  scene.sound.play(audioKey, { volume: 0.28 });
}

/**
 * Apply the persisted audio settings to the live sound manager so a change takes effect
 * immediately (and on reload). Sets the global volume + master mute. When music is disabled
 * (but the master is on) any currently-looping background track is stopped so the toggle is felt
 * right away — SFX are short-lived so they need no explicit stop.
 * @param {Phaser.Scene} scene The Phaser 3 scene to get the sound manager reference from
 * @returns {void}
 */
export function setGlobalSoundSettings(scene) {
  scene.sound.setVolume(dataManager.store.get(DATA_MANAGER_STORE_KEYS.OPTIONS_VOLUME) * 0.25);
  scene.sound.setMute(!masterSoundOn());

  // stop any looping music if the music channel was just turned off (master still on)
  if (masterSoundOn() && !musicOn()) {
    scene.sound.getAllPlaying().forEach((sound) => {
      if (/** @type {any} */ (sound).loop) {
        sound.stop();
      }
    });
  }
}
